import { useEffect, useRef, useState } from 'react';
import { type AnalyticsMode, trackEvent } from '@/shared/lib/analytics';
import { useT } from '@/shared/lib/i18n';
import {
  cycleVariant,
  MUGSHOT_CATEGORIES,
  MUGSHOT_PARTS,
  type MugshotCategory,
  type MugshotConfig,
  randomMugshot,
} from '@/shared/lib/mugshot';
import { AdaptiveDialog } from '../AdaptiveDialog';
import { BrandMark } from '../BrandMark';
import { Button } from '../Button';
import { HandKicker } from '../HandKicker';
import { PaperSheet } from '../PaperSheet';
import { PhaseTag } from '../PhaseTag';
import { Pin } from '../Pin';
import { MugshotPortrait } from './MugshotPortrait';

export type MugshotEditorProps = {
  config: MugshotConfig;
  playerName: string;
  onChange: (next: MugshotConfig) => void;
  onClose: () => void;
  /**
   * When provided, the editor renders an editable name input above the
   * preview and commits the trimmed value on blur / Enter / modal
   * close. Omit (read-only) for cases where the caller doesn't own the
   * name (e.g. viewing another player's dossier).
   */
  onNameChange?: (next: string) => void;
  /** Analytics context — `mode` distinguishes online vs standalone editor sessions. */
  analyticsMode?: AnalyticsMode;
  analyticsRoomId?: string | null;
};

const CATEGORY_KEY: Record<MugshotCategory, string> = {
  menton: 'setup.mugshot.category.menton',
  nez: 'setup.mugshot.category.nez',
  bouche: 'setup.mugshot.category.bouche',
  moustache: 'setup.mugshot.category.moustache',
  yeux: 'setup.mugshot.category.yeux',
  sourcils: 'setup.mugshot.category.sourcils',
  cheveux: 'setup.mugshot.category.cheveux',
  accessoire: 'setup.mugshot.category.accessoire',
};

const variantPosition = (cat: MugshotCategory, id: string): { index: number; total: number } => {
  const variants = MUGSHOT_PARTS[cat];
  const idx = variants.findIndex((v) => v.id === id);
  return { index: idx < 0 ? 0 : idx, total: variants.length };
};

/**
 * Mugshot customizer styled to match the lobby briefing dossier:
 * paper-dossier paper-ruled surface, two corner pins, PhaseTag +
 * BrandMark header, underlined feature rows, orange `<Button>` footer
 * CTA. Backdrop click & Escape both close (handled by AdaptiveDialog).
 */
export const MugshotEditor = ({
  config,
  playerName,
  onChange,
  onClose,
  onNameChange,
  analyticsMode = 'standalone',
  analyticsRoomId = null,
}: MugshotEditorProps) => {
  const t = useT();
  const [draftName, setDraftName] = useState(playerName);
  const changeCountRef = useRef(0);
  const randomizedRef = useRef(false);

  useEffect(() => {
    setDraftName(playerName);
  }, [playerName]);

  // Single `mugshot.configured` event fires on unmount with aggregate state.
  useEffect(() => {
    return () => {
      trackEvent({
        name: 'mugshot.configured',
        data: {
          mode: analyticsMode,
          roomId: analyticsRoomId ?? '',
          changeCount: changeCountRef.current,
          randomized: randomizedRef.current,
        },
      });
    };
  }, [analyticsRoomId, analyticsMode]);

  const commitName = (): void => {
    if (!onNameChange) return;
    const next = draftName.trim();
    if (next.length > 0 && next !== playerName) onNameChange(next);
    else setDraftName(playerName);
  };

  const cycle = (cat: MugshotCategory, dir: 1 | -1) => {
    changeCountRef.current += 1;
    onChange(cycleVariant(config, cat, dir));
  };

  const onRandomize = (): void => {
    changeCountRef.current += 1;
    randomizedRef.current = true;
    onChange(randomMugshot());
  };

  const closeWithCommit = (): void => {
    commitName();
    onClose();
  };

  return (
    <AdaptiveDialog
      open
      onClose={closeWithCommit}
      ariaLabel={t('setup.mugshot.title')}
      closeLabel={t('setup.mugshot.close')}
      surface="bare"
      hideHandle
    >
      <div className="paper-dossier relative flex flex-col gap-5 px-7 py-6 text-ink">
        <Pin left={28} top={-6} />
        <Pin left={'calc(100% - 28px)'} top={-6} />

        <header className="flex flex-col gap-1">
          <PhaseTag size={11} color="rgba(0,0,0,.5)">
            {t('setup.mugshot.phase')}
          </PhaseTag>
          <BrandMark size={'clamp(36px, 4vw, 46px)'} className="mt-1">
            {t('setup.mugshot.title')}
          </BrandMark>
          <HandKicker size={20} color="var(--color-pencil)" rot={0} className="mt-2">
            — {t('setup.mugshot.subtitle')}
          </HandKicker>
        </header>

        <section className="flex flex-col gap-3">
          <PhaseTag size={11} color="var(--color-ink-soft)" tracking={0.2}>
            {t('setup.mugshot.section.identity')}
          </PhaseTag>
          {onNameChange ? (
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitName();
                  (e.currentTarget as HTMLInputElement).blur();
                }
                if (e.key === 'Escape') {
                  setDraftName(playerName);
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
              maxLength={16}
              aria-label="name"
              className="w-full truncate border-ink/85 border-b bg-transparent py-1 font-type text-[18px] text-ink tracking-[0.04em] outline-none focus:border-ink"
            />
          ) : (
            <div className="border-ink/85 border-b py-1 font-type text-[18px] text-ink tracking-[0.04em]">
              {playerName}
            </div>
          )}
          <PaperSheet
            seed={playerName}
            color="var(--color-paper)"
            className="mx-auto aspect-[3/4] w-56 overflow-hidden border-2 border-ink/70 shadow-[3px_4px_0_rgba(0,0,0,0.18)]"
          >
            <MugshotPortrait config={config} className="scale-125" />
          </PaperSheet>
        </section>

        <section>
          <PhaseTag size={11} color="var(--color-ink-soft)" tracking={0.2}>
            {t('setup.mugshot.section.features')}
          </PhaseTag>
          <ul className="mt-2 flex flex-col">
            {MUGSHOT_CATEGORIES.map((cat) => {
              const { index, total } = variantPosition(cat, config[cat]);
              return (
                <li
                  key={cat}
                  className="flex items-center justify-between gap-3 border-ink/85 border-b py-1.5"
                >
                  <span className="font-type text-[15px] text-ink tracking-[0.04em]">
                    {t(CATEGORY_KEY[cat] as never)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => cycle(cat, -1)}
                      aria-label="prev"
                      className="-my-2 flex h-11 w-11 items-center justify-center font-type text-[24px] text-ink/70 leading-none active:text-ink hover:text-ink"
                    >
                      ‹
                    </button>
                    <span className="min-w-[40px] text-center font-mono text-[10px] text-ink/60 tabular-nums tracking-[0.06em]">
                      {index + 1}/{total}
                    </span>
                    <button
                      type="button"
                      onClick={() => cycle(cat, 1)}
                      aria-label="next"
                      className="-my-2 flex h-11 w-11 items-center justify-center font-type text-[24px] text-ink/70 leading-none active:text-ink hover:text-ink"
                    >
                      ›
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <footer className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onRandomize}
            className="font-type text-[14px] text-ink/70 underline-offset-4 hover:text-ink hover:underline"
          >
            {t('setup.mugshot.randomize')}
          </button>
          <Button variant="orange" fullWidth={false} onClick={closeWithCommit}>
            {t('setup.mugshot.done')} →
          </Button>
        </footer>
      </div>
    </AdaptiveDialog>
  );
};
